import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../ui/utils";
import { Button } from "../ui/button";
import { ChartEmptyState } from "../ChartsComponent/ChartStates";

/* ─── Types ──────────────────────────────────────────────────────────────── */

/** One cell in the matrix: `application` on the X-axis, `environment` on the Y-axis, `status` sets the fill. */
export type AgentReportingStatus = "active" | "inactive" | "unreachable" | "never";

export interface AgentReportingEntry {
  application: string;
  environment: string;
  status: AgentReportingStatus;
  /** Number of assets in this pairing, shown on hover. */
  assetCount?: number;
  /** Optional timestamp for the tooltip. */
  timestamp?: string;
  /** Optional extra detail shown on hover. */
  detail?: string;
}

export interface AgentReportingMatrixProps {
  data: AgentReportingEntry[];
  id?: string;
  name?: string;
  /** Height of each row in px. */
  rowHeight?: number;
  /** Half-width of each diamond mark in px. */
  markRadius?: number;
  /** Cap the chart height, enabling vertical scroll for many environments. */
  maxHeight?: number;
  /** Makes the legend clickable to filter by status. */
  interactiveLegend?: boolean;
  /** Clicking a diamond hands back that cell's entry. Omit for a read-only plot. */
  onCellClick?: (entry: AgentReportingEntry) => void;
  /** Title centered above the environment column headers (X-axis). */
  xAxisLabel?: string;
  /** Title beside the application row labels (Y-axis), rotated vertically. */
  yAxisLabel?: string;
}

/* ─── Constants ──────────────────────────────────────────────────────────── */

const LABEL_Y_WIDTH_MIN = 64; // narrowest the Y-axis (application) label gutter can shrink to
const LABEL_Y_WIDTH_MAX = 180; // widest the Y-axis (application) label gutter can grow to
const LABEL_Y_CHAR_WIDTH = 7; // rough advance width of a character at the label's font size
// In scroll mode the diamond grid starts its own SVG at y=0, so this only sizes the header row, not the grid.
const TOP_MARGIN = 16;
const RIGHT_MARGIN = 12;
const LEFT_PAD = 8;
const BOTTOM_MARGIN = 12;
const BAR_HEIGHT = 32;
const MAX_COL_WIDTH = 140; // widest an environment column can grow to before the plot re-centers instead of spreading further
const MIN_COL_WIDTH = 64; // narrowest an environment column can shrink to before the grid scrolls horizontally instead
// Rough advance width of a character as a share of font size -- see the same ratio in RankedBarChart.
const LABEL_CHAR_WIDTH_RATIO = 0.6;

/** Truncates `text` with a trailing "..." once it would overflow `maxWidth` at `fontSize`. */
function truncateLabel(text: string, maxWidth: number, fontSize: number): string {
  const maxChars = Math.max(1, Math.floor(maxWidth / (fontSize * LABEL_CHAR_WIDTH_RATIO)));
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(1, maxChars - 3))}...`;
}

/* Single source of truth per status so a colour means the same thing everywhere it's used. */
export const AGENT_STATUS_META: Record<
  AgentReportingStatus,
  { label: string; color: string }
> = {
  active: { label: "Active", color: "#10b981" },
  inactive: { label: "Inactive", color: "#ec835a" },
  unreachable: { label: "Unreachable", color: "#d03b3b" },
  never: { label: "Never Reported", color: "#94a3b8" },
};

const LEGEND_ENTRIES = Object.entries(AGENT_STATUS_META).map(
  ([status, meta]) => ({
    status: status as AgentReportingStatus,
    label: meta.label,
    color: meta.color,
  }),
);

const LABEL_CSS = `
  .arm-x-label { fill: #334155; }
  .arm-y-label { fill: #334155; }
  .dark .arm-x-label { fill: #e2e8f0; }
  .dark .arm-y-label { fill: #e2e8f0; }
  .arm-grid-line { stroke: #2d3748; }
  .dark .arm-grid-line { stroke: #94a3b8; }
`;

/* ─── Component ──────────────────────────────────────────────────────────── */

export function AgentReportingMatrix({
  data,
  id = "agent-reporting-matrix",
  name,
  rowHeight = 48,
  markRadius = 8,
  maxHeight,
  interactiveLegend = false,
  onCellClick,
  xAxisLabel,
  yAxisLabel,
}: AgentReportingMatrixProps) {
  const resolvedName = name?.trim() || id;
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  const [legendFilter, setLegendFilter] = useState<Set<AgentReportingStatus>>(
    new Set(),
  );
  const [tooltip, setTooltip] = useState<{
    entry: AgentReportingEntry;
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleHide() {
    hideTimer.current = setTimeout(() => setTooltip(null), 150);
  }
  function cancelHide() {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }

  const uniqueSorted = <T,>(items: T[], getValue: (item: T) => string) =>
    Array.from(new Set(items.map(getValue))).sort();

  const environments = useMemo(
    () => uniqueSorted(data, (d) => d.environment),
    [data],
  );

  const applications = useMemo(
    () => uniqueSorted(data, (d) => d.application),
    [data],
  );

  /* Quick lookup: "app|env" → entry */
  const entryMap = useMemo(() => {
    const map = new Map<string, AgentReportingEntry>();
    data.forEach((d) => map.set(`${d.application}|${d.environment}`, d));
    return map;
  }, [data]);

  /* Keyed on app/environment counts so this re-runs once the container actually mounts
     (before data arrives it's replaced by the placeholder below, so containerRef is null). */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    measure();
    const raf = requestAnimationFrame(measure);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [applications.length, environments.length]);

  if (applications.length === 0 || environments.length === 0) {
    return (
      <div id={id} data-name={resolvedName}>
        <ChartEmptyState
          height={maxHeight ?? 240}
          shape="cartesian"
          message="No agents match the selected filters"
        />
      </div>
    );
  }

  // measured `width` includes the Y-axis title column and gap, so subtract it to get the SVGs' own width.
  const yTitleWidth = 18;
  const yTitleGap = 8;
  const yTitleSpace = yAxisLabel ? yTitleWidth + yTitleGap : 0;
  const svgWidth = width - yTitleSpace;
  // Sized to the longest application name present, not a fixed gutter, to avoid a lopsided gap for short names.
  const longestAppLabel = applications.reduce((max, name) => Math.max(max, name.length), 0);
  const labelYWidth = Math.min(
    LABEL_Y_WIDTH_MAX,
    Math.max(LABEL_Y_WIDTH_MIN, longestAppLabel * LABEL_Y_CHAR_WIDTH + 16),
  );
  const plotStartX = labelYWidth + LEFT_PAD;
  const plotW = svgWidth - plotStartX - RIGHT_MARGIN;
  const naturalColWidth = plotW / environments.length;
  const scrolls = typeof maxHeight === "number";
  // Once columns would squeeze narrower than a readable minimum, hold them there and let the
  // grid scroll horizontally instead of continuing to compress -- the application label column
  // stays fixed outside that horizontal scroll area.
  const hScroll = naturalColWidth < MIN_COL_WIDTH;
  const colWidth = hScroll ? MIN_COL_WIDTH : naturalColWidth;
  const bodyH = applications.length * rowHeight;
  const svgH = TOP_MARGIN + bodyH + BOTTOM_MARGIN;
  const hScrollContentWidth = colWidth * environments.length + RIGHT_MARGIN;

  function colCenterX(colIdx: number) {
    return plotStartX + colIdx * colWidth + colWidth / 2;
  }

  function handleMouseEnter(
    e: React.MouseEvent<SVGElement>,
    entry: AgentReportingEntry,
  ) {
    cancelHide();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltip({
      entry,
      mouseX: e.clientX - rect.left,
      mouseY: e.clientY - rect.top,
    });
  }

  return (
    <div
      id={id}
      data-name={resolvedName}
      ref={containerRef}
      // flex-1 + min-h-0 so this fills the parent's stretched height instead of sizing to its own content.
      className="relative flex w-full flex-1 min-h-0 flex-col overflow-hidden"
    >
      {/* Legend styling matches DonutChrome's so legends read identically across the module. */}
      <ul className="mb-0 flex w-max shrink-0 flex-row flex-nowrap items-center gap-x-4 gap-y-1.5">
        {LEGEND_ENTRIES.map(({ status, label, color }) => {
          const hidden = legendFilter.has(status);
          const swatch = (
            <span
              aria-hidden="true"
              className={cn(
                "size-2.5 shrink-0 rounded-[3px]",
                hidden && "ring-1 ring-inset ring-border",
              )}
              style={{
                backgroundColor: hidden ? "transparent" : color,
                boxShadow: hidden
                  ? undefined
                  : `0 0 0 2px color-mix(in srgb, ${color} 20%, transparent)`,
              }}
            />
          );
          const text = (
            <span className="whitespace-nowrap text-[11px] font-medium leading-snug text-muted-foreground">
              {label}
            </span>
          );
          return (
            <li key={status} className="shrink-0">
              {interactiveLegend ? (
                <Button variant="none" size="none"
                  type="button"
                  aria-pressed={!hidden}
                  title={hidden ? `Show ${label}` : `Hide ${label}`}
                  onClick={() =>
                    setLegendFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(status)) {
                        next.delete(status);
                      } else {
                        if (next.size + 1 >= LEGEND_ENTRIES.length) return prev;
                        next.add(status);
                      }
                      return next;
                    })
                  }
                  className={cn(
                    "flex w-full min-w-0 cursor-pointer items-center justify-start gap-2 rounded-md px-1 py-1 text-left transition-opacity hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    hidden && "opacity-40",
                  )}
                >
                  {swatch}
                  {text}
                </Button>
              ) : (
                <div className="flex w-full min-w-0 items-center gap-2 rounded-md px-1 py-1 text-left">
                  {swatch}
                  {text}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Y-axis title sits in its own stretched column beside the SVGs so it centers regardless of scrolling. */}
      <div className="flex min-h-0 flex-1 items-stretch">
        {yAxisLabel && (
          <div
            className="flex shrink-0 items-center justify-center"
            style={{ width: yTitleWidth, marginRight: yTitleGap }}
          >
            <span
              className="whitespace-nowrap text-[11px] font-semibold text-muted-foreground"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              {yAxisLabel}
            </span>
          </div>
        )}
        {hScroll ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div
              className="flex min-w-0"
              style={
                scrolls
                  ? {
                      flex: "1 1 auto",
                      minHeight: 0,
                      maxHeight,
                      overflowY: "auto",
                      overflowX: "hidden",
                      scrollbarGutter: "stable",
                    }
                  : undefined
              }
            >
              {/* Application labels: a normal flex sibling of the horizontally-scrolling grid
                  below, so it never moves horizontally; it scrolls vertically in lockstep with
                  the grid because both share this same vertical scroll box. */}
              <svg
                width={labelYWidth}
                height={bodyH}
                className="shrink-0 bg-card"
                style={{ display: "block" }}
              >
                <style>{LABEL_CSS}</style>
                {applications.map((app, appIdx) => {
                  const cy = appIdx * rowHeight + rowHeight / 2;
                  return (
                    <text
                      key={app}
                      x={labelYWidth - 8}
                      y={cy + 4}
                      textAnchor="end"
                      className="arm-y-label"
                      fontSize={13}
                      fontWeight={600}
                    >
                      <title>{app}</title>
                      {truncateLabel(app, labelYWidth - 16, 13)}
                    </text>
                  );
                })}
              </svg>

              {/* Environment header + diamond grid: scrolls horizontally together as one unit;
                  the header also stays pinned to the top of the vertical scroll box above. */}
              <div className="min-w-0 flex-1 overflow-x-auto" style={{ scrollbarGutter: "stable" }}>
                <div style={{ width: hScrollContentWidth }}>
                  <svg
                    width={hScrollContentWidth}
                    height={TOP_MARGIN}
                    aria-hidden="true"
                    className={cn("shrink-0 bg-card", scrolls && "sticky top-0 z-10")}
                    style={{ display: "block" }}
                  >
                    <style>{LABEL_CSS}</style>
                    {environments.map((env, i) => (
                      <text
                        key={env}
                        x={i * colWidth + colWidth / 2}
                        y={TOP_MARGIN - 3}
                        textAnchor="middle"
                        className="arm-x-label"
                        fontSize={12}
                        fontWeight={600}
                      >
                        <title>{env}</title>
                        {truncateLabel(env, colWidth - 4, 12)}
                      </text>
                    ))}
                  </svg>

                  <svg
                    role="img"
                    aria-label={`Agent reporting matrix: ${environments.length} environments x ${applications.length} applications`}
                    width={hScrollContentWidth}
                    height={bodyH}
                    style={{ display: "block" }}
                    onMouseLeave={scheduleHide}
                  >
                    <style>{LABEL_CSS}</style>
                    {applications.map((app, appIdx) => {
                      const rowY = appIdx * rowHeight;
                      const cy = rowY + rowHeight / 2;
                      const isAlt = appIdx % 2 === 1;

                      return (
                        <g key={app}>
                          {isAlt && (
                            <rect
                              x={0}
                              y={rowY}
                              width={hScrollContentWidth}
                              height={rowHeight}
                              fill="#ffffff"
                              fillOpacity={0.015}
                            />
                          )}

                          <line
                            x1={0}
                            y1={cy}
                            x2={hScrollContentWidth}
                            y2={cy}
                            className="arm-grid-line"
                            strokeDasharray="2 4"
                            strokeOpacity={0.5}
                          />

                          {environments.map((_, colIdx) => (
                            <line
                              key={colIdx}
                              x1={colIdx * colWidth + colWidth / 2}
                              y1={rowY}
                              x2={colIdx * colWidth + colWidth / 2}
                              y2={rowY + rowHeight}
                              className="arm-grid-line"
                              strokeDasharray="3 3"
                              strokeOpacity={0.2}
                            />
                          ))}

                          {environments.map((env, colIdx) => {
                            const entry = entryMap.get(`${app}|${env}`);
                            if (!entry) return null;

                            const cx = colIdx * colWidth + colWidth / 2;
                            const baseColor = AGENT_STATUS_META[entry.status].color;
                            const excluded = legendFilter.has(entry.status);
                            const r = markRadius;
                            const diamondPoints = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;

                            return (
                              <polygon
                                key={`${env}-${app}`}
                                points={diamondPoints}
                                fill={baseColor}
                                stroke="var(--diamond-stroke, #0d1117)"
                                strokeWidth={1.5}
                                opacity={excluded ? 0.2 : 1}
                                style={{
                                  cursor: onCellClick ? "pointer" : "default",
                                  transition: "opacity 0.2s",
                                  pointerEvents: excluded ? "none" : undefined,
                                }}
                                onClick={onCellClick ? () => onCellClick(entry) : undefined}
                                onMouseEnter={(e) => handleMouseEnter(e, entry)}
                                onMouseMove={(e) => {
                                  const container = containerRef.current;
                                  if (!container) return;
                                  const rect = container.getBoundingClientRect();
                                  setTooltip((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          mouseX: e.clientX - rect.left,
                                          mouseY: e.clientY - rect.top,
                                        }
                                      : prev,
                                  );
                                }}
                                onMouseLeave={scheduleHide}
                              />
                            );
                          })}
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>
            </div>

            {xAxisLabel && (
              <div
                className="shrink-0 text-center text-[11px] font-semibold text-muted-foreground"
                style={{ marginLeft: labelYWidth, marginTop: 23 }}
              >
                {xAxisLabel}
              </div>
            )}
          </div>
        ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Header + grid center together when rows fit within maxHeight; once content overflows,
              the header sticks to the top of the scroll area instead. */}
          <div
            style={
              scrolls
                ? {
                    // Grows to fill available room (capped at maxHeight) so the centering below has real space to work with.
                    flex: "1 1 auto",
                    minHeight: 0,
                    maxHeight,
                    overflowY: "auto",
                    overflowX: "hidden",
                    position: "relative",
                    scrollbarGutter: "stable",
                    display: "flex",
                    flexDirection: "column",
                    // "safe" falls back to start alignment when content overflows, so the top is never clipped.
                    justifyContent: "safe center",
                  }
                : undefined
            }
          >
            {scrolls && (
              <svg
                width={svgWidth}
                height={TOP_MARGIN}
                aria-hidden="true"
                className="sticky top-0 z-10 shrink-0 bg-card"
                style={{ display: "block", scrollbarGutter: "stable" }}
              >
                <style>{LABEL_CSS}</style>
                {environments.map((env, i) => (
                  <text
                    key={env}
                    x={colCenterX(i)}
                    y={TOP_MARGIN - 3}
                    textAnchor="middle"
                    className="arm-x-label"
                    fontSize={12}
                    fontWeight={600}
                  >
                    <title>{env}</title>
                    {truncateLabel(env, colWidth - 4, 12)}
                  </text>
                ))}
              </svg>
            )}

            <svg
              role="img"
              aria-label={`Agent reporting matrix: ${environments.length} environments × ${applications.length} applications`}
              width={svgWidth}
              height={scrolls ? bodyH + BOTTOM_MARGIN : svgH}
              style={{ display: "block" }}
              onMouseLeave={scheduleHide}
            >
              <style>{LABEL_CSS}</style>

          {!scrolls &&
            environments.map((env, i) => (
              <text
                key={env}
                x={colCenterX(i)}
                y={TOP_MARGIN - 3}
                textAnchor="middle"
                className="arm-x-label"
                fontSize={12}
                fontWeight={600}
              >
                <title>{env}</title>
                {truncateLabel(env, colWidth - 4, 12)}
              </text>
            ))}

          {applications.map((app, appIdx) => {
            const rowY = (scrolls ? 0 : TOP_MARGIN) + appIdx * rowHeight;
            const cy = rowY + rowHeight / 2;
            const isAlt = appIdx % 2 === 1;

            return (
              <g key={app}>
                {isAlt && (
                  <rect
                    x={plotStartX}
                    y={rowY}
                    width={plotW}
                    height={rowHeight}
                    fill="#ffffff"
                    fillOpacity={0.015}
                  />
                )}

                <text
                  x={labelYWidth - 8}
                  y={cy + 4}
                  textAnchor="end"
                  className="arm-y-label"
                  fontSize={13}
                  fontWeight={600}
                >
                  <title>{app}</title>
                  {truncateLabel(app, labelYWidth - 16, 13)}
                </text>

                <line
                  x1={plotStartX}
                  y1={cy}
                  x2={plotStartX + plotW}
                  y2={cy}
                  className="arm-grid-line"
                  strokeDasharray="2 4"
                  strokeOpacity={0.5}
                />

                {environments.map((_, colIdx) => (
                  <line
                    key={colIdx}
                    x1={colCenterX(colIdx)}
                    y1={rowY}
                    x2={colCenterX(colIdx)}
                    y2={rowY + rowHeight}
                    className="arm-grid-line"
                    strokeDasharray="3 3"
                    strokeOpacity={0.2}
                  />
                ))}

                {environments.map((env, colIdx) => {
                  const entry = entryMap.get(`${app}|${env}`);
                  if (!entry) return null;

                  const cx = colCenterX(colIdx);
                  const baseColor = AGENT_STATUS_META[entry.status].color;
                  const excluded = legendFilter.has(entry.status);
                  const r = markRadius;
                  const diamondPoints = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;

                  return (
                    <polygon
                      key={`${env}-${app}`}
                      points={diamondPoints}
                      fill={baseColor}
                      stroke="var(--diamond-stroke, #0d1117)"
                      strokeWidth={1.5}
                      opacity={excluded ? 0.2 : 1}
                      style={{
                        cursor: onCellClick ? "pointer" : "default",
                        transition: "opacity 0.2s",
                        pointerEvents: excluded ? "none" : undefined,
                      }}
                      onClick={onCellClick ? () => onCellClick(entry) : undefined}
                      onMouseEnter={(e) => handleMouseEnter(e, entry)}
                      onMouseMove={(e) => {
                        const container = containerRef.current;
                        if (!container) return;
                        const rect = container.getBoundingClientRect();
                        setTooltip((prev) =>
                          prev
                            ? {
                                ...prev,
                                mouseX: e.clientX - rect.left,
                                mouseY: e.clientY - rect.top,
                              }
                            : prev,
                        );
                      }}
                      onMouseLeave={scheduleHide}
                    />
                  );
                })}
              </g>
            );
          })}
            </svg>
          </div>

          {xAxisLabel && (
            <div
              className="shrink-0 text-center text-[11px] font-semibold text-muted-foreground"
              style={{ marginLeft: plotStartX, width: plotW, marginTop: 23 }}
            >
              {xAxisLabel}
            </div>
          )}
        </div>
        )}
      </div>

      {tooltip &&
        (() => {
          const { entry } = tooltip;
          const meta = AGENT_STATUS_META[entry.status];
          // Tooltip is w-52 (208px); flip it to the cursor's left near the right edge
          // instead of clamping it in place, which would leave it sitting under the
          // cursor and make the hover hit-test flicker between the diamond and the tip.
          const TOOLTIP_WIDTH = 208;
          const tipX =
            tooltip.mouseX + 12 + TOOLTIP_WIDTH > width
              ? Math.max(4, tooltip.mouseX - 12 - TOOLTIP_WIDTH)
              : tooltip.mouseX + 12;
          const tipY = tooltip.mouseY - 10;
          return (
            <div
              className="absolute z-30"
              style={{ left: tipX, top: tipY }}
              onMouseEnter={cancelHide}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="w-52 rounded-xl border border-white/[0.12] bg-[#0d0f1a] p-3 shadow-2xl">
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: meta.color }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: meta.color }}
                  >
                    {meta.label}
                  </span>
                </div>
                {entry.assetCount != null && (
                  <p className="text-[11px] text-gray-300">
                    Assets: <span className="font-semibold">{entry.assetCount}</span>
                  </p>
                )}
              </div>
            </div>
          );
        })()}
    </div>
  );
}
